import i18next from "i18next";
import { makeAutoObservable } from "mobx";
import { NotificationsStore } from "stores/NotificationsStore";
import { DiscountData } from "types/purchaseMembership";
import { getErrorMessage } from "utilities/ErrorHelper";
import { TestOneTimePaymentService } from "./TestOneTimePaymentService";
import { ShopCertificationTest } from "./types";

export class TestOneTimePaymentStore {
  constructor(private readonly service: TestOneTimePaymentService, private readonly ns: NotificationsStore) {
    makeAutoObservable(this);
  }

  isTokenHandling: boolean = false;
  tokenId: string = '';
  tokenHandlingError: string = '';
  ThreeDSecureToken: string = '';
  ThreeDSecureError: string = '';
  subscriptionPurchased: boolean = false;

  discountData: DiscountData = {
    couponCode: '',
    discount: '',
    currencySymbol: '',
  };
  subscriptionEmail: string = '';
  shopCertificationTests: ShopCertificationTest[] = [];
  isLoadingCertificationTests: boolean = false;
  certificationTestsError: string = '';

  tokenHandler = async (
    email: string,
    subscriptionPlanCode: string | null,
    currency: string,
    couponCode: string,
    includeSubscription: boolean,
    productIds: string[]
  ) => {
    try {
      this.tokenHandlingError = '';
      this.isTokenHandling = true;
      const response = await this.service.tokenHandler(
        this.tokenId,
        email,
        subscriptionPlanCode,
        currency,
        couponCode,
        includeSubscription,
        productIds
      );

      if (response.status === 'tokenReturned') {
        this.ThreeDSecureToken = response.token;
      } else if (response.status === 'success') {
        this.subscriptionPurchased = response.isSubscriptionPurchased;
        this.isTokenHandling = false; // Loading state should stop only if token is not returned
      } else {
        this.subscriptionPurchased = response.isSubscriptionPurchased;
        this.tokenHandlingError = response.errorMessage.detail;
        this.isTokenHandling = false; // Loading state should stop only if token is not returned
      }
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
  };

  sendThreeDSecureRequest = async (
    tokenId: string,
    email: string,
    subscriptionPlanCode: string | null,
    currency: string,
    couponCode: string,
    threeDSecureTokenId: string,
    includeSubscription: boolean,
    productIds: string[]
  ) => {
    try {
      this.isTokenHandling = true;

      const response = await this.service.sendThreeDSecureRequest(
        tokenId,
        email,
        subscriptionPlanCode,
        currency,
        couponCode,
        threeDSecureTokenId,
        includeSubscription,
        productIds
      );

      if (response.data && response.status !== 200) {
        this.ThreeDSecureError = response.data.detail;
      } else if (response.data && response.data.data.isError) {
        this.ThreeDSecureError = i18next.t('PurchaseSubscriptionError', { ns: 'purchaseMembership' });
      } else {
        this.subscriptionPurchased = response.data.data;
      }
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.isTokenHandling = false;
  };

  fetchShopCertificationTests = async () => {
    try {
      this.isLoadingCertificationTests = true;
      this.certificationTestsError = '';
      const response = await this.service.getShopCertificationTests();
      this.shopCertificationTests = response.shopCertificationTests || [];
    } catch (ex) {
      this.certificationTestsError = getErrorMessage(ex);
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    } finally {
      this.isLoadingCertificationTests = false;
    }
  };
}
